import { FC, useState, useEffect } from 'react';
import { Table, Tag, Input, Select, DatePicker, Space, Button, Card, Typography, message, Modal, Form, Checkbox } from 'antd';
import { SearchOutlined, FilterOutlined, ReloadOutlined, MailOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { notificationsAPI } from '../../../../../services/notifications/notifications.api';
import { TNotification, NotificationType } from '../../../../../types/notification';
import { formatMentions } from '../../../../../utils/formatMentions';
import { useEmailConfirmation } from '../../../../../hooks/useEmailConfirmation';
import { ManualMessageModal } from './manual-message-modal';

dayjs.extend(relativeTime);

const { RangePicker } = DatePicker;
const { Text } = Typography;

export const SystemLogsTab: FC = () => {
  const [notifications, setNotifications] = useState<TNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [searchUser, setSearchUser] = useState('');
  const [filterType, setFilterType] = useState<NotificationType | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const { showConfirmation, ConfirmationModal } = useEmailConfirmation();
  const [isManualMessageModalOpen, setIsManualMessageModalOpen] = useState(false);

  useEffect(() => {
    fetchAllNotifications();
  }, [page, pageSize, filterType]);

  const fetchAllNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationsAPI.getNotifications({
        page,
        pageSize,
        notificationType: filterType,
      });
      setNotifications(response.items);
      setTotal(response.meta.totalItems);
    } catch (error) {
      console.error('Failed to fetch system logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchAllNotifications();
  };

  const handleClearFilters = () => {
    setSearchUser('');
    setFilterType(undefined);
    setDateRange(null);
    setPage(1);
  };

  const handleResendNotification = (notification: TNotification) => {
    if (!notification.user) {
      message.error('No recipient found for this notification');
      return;
    }

    const recipients = [
      {
        email: notification.user.email || '',
        name: `${notification.user.firstName} ${notification.user.lastName}`,
        role: notification.user.role,
      },
    ];

    showConfirmation(recipients, {
      actionDescription: `Resending notification: ${notification.title}`,
      onProceed: async (sendEmails: boolean) => {
        if (!sendEmails) {
          message.info('Resend cancelled');
          return;
        }

        try {
          const result = await notificationsAPI.resendNotification(notification.id);

          if (result.success) {
            message.success('Email notification resent successfully');
          } else {
            message.error(result.message || 'Failed to resend email');
          }
        } catch (error: any) {
          console.error('Error resending notification:', error);
          message.error(error?.response?.data?.message || 'Failed to resend email notification');
        }
      },
    });
  };

  const getTypeColor = (type: NotificationType): string => {
    switch (type) {
      case NotificationType.MENTION:
        return 'blue';
      case NotificationType.JOB_ASSIGNMENT:
        return 'orange';
      case NotificationType.SERVICE_TICKET_ASSIGNMENT:
        return 'purple';
      case NotificationType.TASK_ASSIGNMENT:
        return 'green';
      case NotificationType.TASK_UPDATE:
        return 'cyan';
      case NotificationType.JOB_UPDATE:
        return 'magenta';
      case NotificationType.EMAIL:
        return 'geekblue';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type: NotificationType): string => {
    switch (type) {
      case NotificationType.MENTION:
        return 'Mention';
      case NotificationType.JOB_ASSIGNMENT:
        return 'Job Assignment';
      case NotificationType.SERVICE_TICKET_ASSIGNMENT:
        return 'Service Assignment';
      case NotificationType.TASK_ASSIGNMENT:
        return 'Task Assignment';
      case NotificationType.TASK_UPDATE:
        return 'Task Update';
      case NotificationType.JOB_UPDATE:
        return 'Job Update';
      case NotificationType.EMAIL:
        return 'Email';
      default:
        return 'General';
    }
  };

  const filteredNotifications = notifications.filter((notification) => {
    // Filter by user search
    if (searchUser) {
      const userMatch =
        notification.user?.firstName?.toLowerCase().includes(searchUser.toLowerCase()) ||
        notification.user?.lastName?.toLowerCase().includes(searchUser.toLowerCase()) ||
        notification.user?.username?.toLowerCase().includes(searchUser.toLowerCase());

      const triggeredByMatch =
        notification.triggeredByUser?.firstName?.toLowerCase().includes(searchUser.toLowerCase()) ||
        notification.triggeredByUser?.lastName?.toLowerCase().includes(searchUser.toLowerCase());

      if (!userMatch && !triggeredByMatch) return false;
    }

    // Filter by date range
    if (dateRange && dateRange[0] && dateRange[1]) {
      const notificationDate = dayjs(notification.createdAt);
      if (notificationDate.isBefore(dateRange[0]) || notificationDate.isAfter(dateRange[1])) {
        return false;
      }
    }

    return true;
  });

  const columns = [
    {
      title: 'Date/Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: Date) => (
        <div>
          <div>{dayjs(date).format('MMM D, YYYY')}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(date).format('h:mm A')} ({dayjs(date).fromNow()})
          </Text>
        </div>
      ),
      sorter: (a: TNotification, b: TNotification) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Type',
      dataIndex: 'notificationType',
      key: 'notificationType',
      width: 150,
      render: (type: NotificationType) => (
        <Tag color={getTypeColor(type)}>{getTypeLabel(type)}</Tag>
      ),
    },
    {
      title: 'Recipient',
      dataIndex: 'user',
      key: 'user',
      width: 200,
      render: (user: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {user?.firstName} {user?.lastName}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            @{user?.username}
          </Text>
        </div>
      ),
    },
    {
      title: 'Message',
      key: 'message',
      render: (record: TNotification) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>{record.title}</div>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {formatMentions(record.content)}
          </Text>
        </div>
      ),
    },
    {
      title: 'Triggered By',
      dataIndex: 'triggeredByUser',
      key: 'triggeredByUser',
      width: 180,
      render: (triggeredBy: any) =>
        triggeredBy ? (
          <div>
            <div>
              {triggeredBy.firstName} {triggeredBy.lastName}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              @{triggeredBy.username}
            </Text>
          </div>
        ) : (
          <Text type="secondary">System</Text>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'isRead',
      key: 'isRead',
      width: 100,
      render: (isRead: boolean) => (
        <Tag color={isRead ? 'green' : 'orange'}>
          {isRead ? 'Read' : 'Unread'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (record: TNotification) => {
        // Don't show resend button for EMAIL type (manual messages)
        if (record.notificationType === NotificationType.EMAIL) {
          return <span style={{ color: '#52c41a' }}>✓ Sent</span>;
        }

        return (
          <Button
            icon={<MailOutlined />}
            size="small"
            onClick={() => handleResendNotification(record)}
            disabled={!record.user?.email}
            title={record.user?.email ? 'Resend email notification' : 'No email address available'}
          >
            Resend
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <ConfirmationModal />
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong style={{ fontSize: 16 }}>
              System Notification Logs
            </Text>
            <Space>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => setIsManualMessageModalOpen(true)}
              >
                Send Manual Message
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                Refresh
              </Button>
              <Button onClick={handleClearFilters}>Clear Filters</Button>
            </Space>
          </div>

          <Space wrap style={{ width: '100%' }}>
            <Input
              placeholder="Search by user..."
              prefix={<SearchOutlined />}
              value={searchUser}
              onChange={(e) => setSearchUser(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />

            <Select
              placeholder="Filter by type"
              value={filterType}
              onChange={setFilterType}
              style={{ width: 200 }}
              allowClear
              suffixIcon={<FilterOutlined />}
            >
              <Select.Option value={NotificationType.EMAIL}>
                <Tag color="geekblue">Email</Tag>
              </Select.Option>
              <Select.Option value={NotificationType.MENTION}>
                <Tag color="blue">Mention</Tag>
              </Select.Option>
              <Select.Option value={NotificationType.JOB_ASSIGNMENT}>
                <Tag color="orange">Job Assignment</Tag>
              </Select.Option>
              <Select.Option value={NotificationType.SERVICE_TICKET_ASSIGNMENT}>
                <Tag color="purple">Service Assignment</Tag>
              </Select.Option>
              <Select.Option value={NotificationType.TASK_ASSIGNMENT}>
                <Tag color="green">Task Assignment</Tag>
              </Select.Option>
              <Select.Option value={NotificationType.TASK_UPDATE}>
                <Tag color="cyan">Task Update</Tag>
              </Select.Option>
              <Select.Option value={NotificationType.JOB_UPDATE}>
                <Tag color="magenta">Job Update</Tag>
              </Select.Option>
            </Select>

            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="MMM D, YYYY"
              style={{ width: 280 }}
            />
          </Space>

          <div>
            <Text type="secondary">
              Showing {filteredNotifications.length} of {total} total notifications
            </Text>
          </div>
        </Space>
      </Card>

      <Table
        columns={columns}
        dataSource={filteredNotifications}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            setPageSize(newPageSize || 50);
          },
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} notifications`,
          pageSizeOptions: ['10', '25', '50', '100', '200'],
        }}
        scroll={{ x: 1200 }}
        size="middle"
      />

      <ManualMessageModal
        open={isManualMessageModalOpen}
        onClose={() => setIsManualMessageModalOpen(false)}
        onSuccess={() => {
          message.success('Message sent successfully!');
          setIsManualMessageModalOpen(false);
          handleRefresh();
        }}
      />
    </div>
  );
};
